"use client";

import { VolumeX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type HeroVideoSource = {
  src: string;
  type: string;
};

type HeroVideoProps = {
  sources: HeroVideoSource[];
  videoTitle: string;
  overlayLabel: string;
};

/**
 * Interactive hero video: stays out of the initial DOM until first paint is
 * done, then mounts the `<video>` and starts loading. Keeps the video off the
 * LCP critical path so the heading text becomes the LCP candidate (paints from
 * server HTML, no asset wait).
 */
export default function HeroVideo({ sources, videoTitle, overlayLabel }: HeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldMount, setShouldMount] = useState(false);
  const [isOverlayFadingOut, setIsOverlayFadingOut] = useState(false);
  const [isOverlayHidden, setIsOverlayHidden] = useState(false);
  const overlayTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Mount the video after the browser is idle so it never competes with the
    // initial paint for bandwidth or main-thread work. requestIdleCallback isn't
    // in Safari yet — fall back to setTimeout.
    type IdleHandle = number;
    type IdleScheduler = (cb: () => void, opts?: { timeout: number }) => IdleHandle;
    type IdleCanceller = (handle: IdleHandle) => void;

    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: IdleScheduler;
        cancelIdleCallback?: IdleCanceller;
      };
    const schedule: IdleScheduler =
      win.requestIdleCallback ??
      ((cb: () => void) => window.setTimeout(cb, 200) as unknown as IdleHandle);
    const cancel: IdleCanceller =
      win.cancelIdleCallback ?? ((id: IdleHandle) => window.clearTimeout(id));

    const handle = schedule(() => setShouldMount(true), { timeout: 1500 });
    return () => cancel(handle);
  }, []);

  useEffect(() => {
    return () => {
      if (overlayTimerRef.current != null) window.clearTimeout(overlayTimerRef.current);
    };
  }, []);

  const handleUnmute = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement || isOverlayHidden) return;

    if (overlayTimerRef.current != null) {
      window.clearTimeout(overlayTimerRef.current);
    }

    videoElement.muted = false;
    videoElement.currentTime = 0;
    videoElement.loop = false;
    videoElement.controls = true;
    void videoElement.play();

    setIsOverlayFadingOut(true);
    overlayTimerRef.current = window.setTimeout(() => setIsOverlayHidden(true), 380);
  }, [isOverlayHidden]);

  return (
    <>
      {shouldMount && (
        <video
          autoPlay
          className="absolute top-0 left-0 block h-full w-full border-0 object-cover"
          id="ag-hero-video"
          loop
          muted
          playsInline
          preload="none"
          ref={videoRef}
          title={videoTitle}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      )}

      {!isOverlayHidden && (
        <button
          aria-label={overlayLabel}
          className={`absolute inset-0 z-10 flex cursor-pointer items-center justify-center bg-transparent transition-opacity duration-[350ms] ease-in-out ${
            isOverlayFadingOut ? "pointer-events-none opacity-0" : "opacity-100"
          }`}
          id="ag-unmute-overlay"
          onClick={handleUnmute}
          type="button"
        >
          <span className="text-foreground pointer-events-none flex items-center gap-3 rounded-[2px] bg-[hsl(var(--background)/0.95)] px-[clamp(18px,3vw,32px)] py-[clamp(10px,2vw,16px)] font-sans text-[clamp(12px,2vw,16px)] font-semibold tracking-[0.08em] whitespace-nowrap uppercase shadow-[0_4px_12px_rgba(0,0,0,0.15)]">
            <VolumeX className="h-[22px] w-[22px] shrink-0" strokeWidth={2.5} />
            <span>{overlayLabel}</span>
          </span>
        </button>
      )}
    </>
  );
}
